import React, { useState } from "react";
import styled from "styled-components";

// Styled Components
const Overlay = styled.div`
  position: fixed;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(0, 0, 0, 0.6); /* Semi-transparent black background */
  z-index: 1000;
`;

const ModalContainer = styled.div`
  width: 312px;
  padding: 24px;
  background-color: #004d4d; /* Dark teal */
  border-radius: 16px;
  display: flex;
  flex-direction: column; /* Stack items vertically */
  justify-content: flex-start;
  align-items: center;
  box-shadow: 0px 6px 16px rgba(0, 0, 0, 0.2); /* Subtle shadow */
  position: relative;
`;

const ModalTitle = styled.h2`
  font-family: Verdana, sans-serif;
  font-size: 18px;
  font-weight: bold;
  color: #d9ffff; /* Light teal */
  text-align: center;
  margin-bottom: 16px;
`;

const MessageContainer = styled.div`
  font-family: Verdana, sans-serif;
  font-size: 16px;
  color: #acbfbf; /* Muted teal text */
  line-height: 1.5;
  text-align: center; /* Center-align text */
  margin-bottom: 24px;

  .highlight {
    font-style: italic;
    color: #0af; /* Bright teal for emphasis */
  }
`;

const BoxImage = styled.img`
  display: block;
  width: 55%;
  height: auto;
  max-width: 100%;
  margin-bottom: 24px; /* Add space below the image */
`;

const ActionButtons = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
`;

const ConfirmButton = styled.button`
  flex: 1;
  padding: 8px;
  background-color: #10b981; /* Emerald green */
  color: white;
  border-radius: 6px;
  font-size: 12px;
  font-family: Verdana, sans-serif;
  font-weight: bold;
  border: none;
  cursor: pointer;
  margin-right: 8px;
  transition: background-color 0.3s ease;

  &:hover {
    background-color: #059669; /* Darker emerald */
  }
`;

const CancelButton = styled.button`
  flex: 1;
  padding: 8px;
  background-color: transparent;
  color: white; /* White text */
  border: 1px solid white; /* White border */
  border-radius: 6px;
  font-size: 12px;
  font-family: Verdana, sans-serif;
  font-weight: bold;
  cursor: pointer;
  margin-left: 8px;
  transition: background-color 0.3s ease;

  &:hover {
    background-color: rgba(255, 255, 255, 0.2); /* Light overlay */
  }
`;

// Custom Hook
const useConfirmation = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [title, setTitle] = useState<string>("Proceed with Sending?");
  const [message, setMessage] = useState<React.ReactNode>(null);
  const [boxImageUrl, setBoxImageUrl] = useState<string | null>(null);
  const [resolve, setResolve] = useState<((value: boolean) => void) | null>(null);

  const confirm = (
    message: React.ReactNode,
    imageUrl?: string,
    customTitle?: string
  ): Promise<boolean> => {
    return new Promise((resolve) => {
      setMessage(message);
      setBoxImageUrl(imageUrl || null); // Set box image URL if provided
      setTitle(customTitle || "Proceed with Sending?"); // Use custom title if provided
      setIsVisible(true);
      setResolve(() => resolve);
    });
  };

  const handleConfirm = () => {
    if (resolve) resolve(true);
    setIsVisible(false);
  };

  const handleCancel = () => {
    if (resolve) resolve(false);
    setIsVisible(false);
  };

  const ConfirmationModal: React.FC = () => {
    if (!isVisible) return null;

    return (
      <Overlay>
        <ModalContainer>
        <ModalTitle>{title}</ModalTitle> {/* Use dynamic title */}
        <MessageContainer>{message}</MessageContainer> {/* Render passed message */}
          {boxImageUrl && <BoxImage src={boxImageUrl} alt="Box" />}
          <ActionButtons>
            <ConfirmButton onClick={handleConfirm}>Confirm</ConfirmButton>
            <CancelButton onClick={handleCancel}>Cancel</CancelButton>
          </ActionButtons>
        </ModalContainer>
      </Overlay>
    );
  };

  return { confirm, ConfirmationModal };
};

export default useConfirmation;
